'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import CostOfInactionTicker from '@/app/components/CostOfInactionTicker';
import IMFBenchmarks from '@/app/components/IMFBenchmarks';
import WhatIfScenarioTool from '@/app/components/WhatIfScenarioTool';
import RevenueGapEvidence from '@/app/components/RevenueGapEvidence';
import { AlertTriangle, TrendingUp, Globe, Zap, ChevronRight, Target, BookOpen } from 'lucide-react';
import Link from 'next/link';

const sections = [
  { id: 'inaction', label: 'Cost of Inaction', icon: AlertTriangle, color: '#f87171' },
  { id: 'potential', label: 'Untapped Potential', icon: TrendingUp, color: '#34d399' },
  { id: 'benchmarks', label: 'IMF & WB Benchmarks', icon: Globe, color: '#60a5fa' },
  { id: 'whatif', label: 'What-If Scenario Tool', icon: Zap, color: '#fbbf24' },
];

export default function ProblemSolutionClient() {
  const [activeSection, setActiveSection] = useState<string>('inaction');

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Link href="/" className="text-xs flex items-center gap-1 hover:opacity-80 transition-opacity"
              style={{ color: 'var(--dark-muted)' }}>
              Dashboard
            </Link>
            <ChevronRight size={12} style={{ color: 'var(--dark-muted)' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>Problem & Solution</span>
          </div>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, rgba(248,113,113,0.2) 0%, rgba(251,191,36,0.2) 100%)', border: '1px solid rgba(248,113,113,0.3)' }}>
                <AlertTriangle size={22} style={{ color: '#f87171' }} />
              </div>
              <div>
                <h1 className="text-2xl font-black" style={{ color: 'var(--dark-text)' }}>
                  The Problem & The Solution
                </h1>
                <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                  Liberia's property tax gap · IMF benchmarks · Revenue potential · Interactive rollout modelling
                </p>
              </div>
            </div>
            <div className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.25)' }}>
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#ef4444' }} />
              $19.26M/yr Revenue Gap — Live
            </div>
          </div>
        </div>

        {/* Executive Summary Banner */}
        <div className="rounded-2xl p-5 mb-6" style={{
          background: 'linear-gradient(135deg, rgba(248,113,113,0.08) 0%, rgba(251,191,36,0.06) 50%, rgba(52,211,153,0.06) 100%)',
          border: '1px solid rgba(248,113,113,0.2)'
        }}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#f87171' }}>The Core Problem</p>
              <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--dark-text)' }}>
                Liberia collects only 21% of its property tax potential
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                With fewer than 11% of taxable properties registered, Liberia forfeits $19.26M every year. 
                The IMF identifies property tax reform as Liberia's highest-leverage fiscal opportunity. 
                Six African nations have proven the model works — spending a combined $497M+ to build what LRA Nexus delivers.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:col-span-2">
              {[
                { label: 'Current Collection', value: '$5.14M', sub: 'FY2024 actual', color: '#f87171' },
                { label: 'Full Potential', value: '$24.4M', sub: 'At 100% registration', color: '#34d399' },
                { label: 'Annual Gap', value: '$19.26M', sub: 'Lost every year', color: '#fbbf24' },
                { label: 'IMF Target', value: '0.5% GDP', sub: 'Minimum benchmark', color: '#60a5fa' },
              ]?.map((stat) => (
                <div key={stat?.label} className="rounded-xl p-3" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                  <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{stat?.label}</p>
                  <p className="text-xl font-black" style={{ color: stat?.color }}>{stat?.value}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{stat?.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {sections?.map((s) => {
            const SIcon = s?.icon;
            return (
              <button key={s?.id} onClick={() => setActiveSection(s?.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{
                  backgroundColor: activeSection === s?.id ? s?.color + '20' : 'var(--dark-card)',
                  color: activeSection === s?.id ? s?.color : 'var(--dark-muted)',
                  border: `1px solid ${activeSection === s?.id ? s?.color + '40' : 'var(--dark-border)'}`,
                }}>
                <SIcon size={14} />
                {s?.label}
              </button>
            );
          })}
        </div>

        {/* Section Content */}
        {activeSection === 'inaction' && (
          <div>
            <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.15)' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#f87171' }}>Section 1 of 4 — Cost of Inaction</p>
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                Every day without LRA Nexus, Liberia loses $52,767 in uncollected property tax revenue. 
                This live counter shows the real-time cost of delay.
              </p>
            </div>
            <CostOfInactionTicker />
          </div>
        )}

        {activeSection === 'potential' && (
          <div>
            <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#34d399' }}>Section 2 of 4 — Liberia Property Tax: The Untapped Potential</p>
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                County-by-county analysis of registered vs unregistered properties, estimated tax recovery, 
                and the revenue gap LRA Nexus closes — backed by LRA field data.
              </p>
            </div>
            <RevenueGapEvidence />
          </div>
        )}

        {activeSection === 'benchmarks' && (
          <div>
            <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.15)' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#60a5fa' }}>Section 3 of 4 — IMF & World Bank Benchmarks</p>
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                How Liberia compares to IMF minimum standards and African peer nations. 
                Six countries have proven this model works — spending $497M+ combined to build what LRA Nexus delivers.
              </p>
            </div>
            <IMFBenchmarks />
          </div>
        )}

        {activeSection === 'whatif' && (
          <div>
            <div className="mb-4 p-4 rounded-xl" style={{ backgroundColor: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#fbbf24' }}>Section 4 of 4 — What-If Rollout Scenario Tool</p>
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                Model different rollout strategies interactively. Select counties, set target registration rates, 
                choose timelines — and see projected revenue impact recalculated in real time.
              </p>
            </div>
            <WhatIfScenarioTool />
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-8 rounded-2xl p-6" style={{
          background: 'linear-gradient(135deg, rgba(212,160,23,0.1) 0%, rgba(52,211,153,0.06) 100%)',
          border: '1px solid rgba(212,160,23,0.25)'
        }}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--accent)' }}>The Conclusion Is Clear</p>
              <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--dark-text)' }}>
                LRA Nexus is the solution. The research is done. The model is proven. The system is ready.
              </h3>
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>
                Six African nations spent $497M+ building what LRA Nexus delivers. 
                The IMF has identified Liberia's property tax gap as the highest-leverage fiscal reform available.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <Link href="/nine-circle-methodology"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}>
                <BookOpen size={15} />
                View Full Methodology
              </Link>
              <Link href="/property-tax-cycle"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}>
                <Target size={15} />
                Interactive Tax Cycle
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
